(ns frontend.view.nev.sales-opportunity.view
  (:require
   [clojure.string :as string]
   [common.entity :as entity]
   [common.localization :as loc]
   [common.schema.field :as field]
   [frontend.actions.common :as common-actions]
   [frontend.actions.sales-opportunity :as sales-opportunity-actions]
   [frontend.db :as app-db]
   [frontend.domain.sales-opportunity.actions :as sales-opportunity-domain-actions]
   [frontend.logging :as logging]
   [frontend.page-components.default-page-layout :as default-page-layout]
   [frontend.page-components.zap-toolbar :as zap-toolbar]
   [frontend.routing :as routing]
   [frontend.state.current-session :as session]
   [frontend.state.permissions :as f-permissions]
   [frontend.tenant-customizations :as tenant-customizations]
   [frontend.utils :as fu]
   [frontend.view.entity-viewers.components :as nev-components]
   [frontend.view.entity-viewers.entity-editor :as entity-editor]
   [frontend.view.entity-viewers.event-pane :as event-pane]
   [frontend.view.entity-viewers.header :as header]
   [frontend.view.entity-viewers.modules :as modules]
   [frontend.view.entity-viewers.modules.associated-entities :as associated-entities]
   [frontend.view.entity-viewers.modules.check-list.view :as check-list-module]
   [frontend.view.entity-viewers.utils :as entity-utils]
   [frontend.view.field-templates :as templates]
   [frontend.view.modals.change-pipeline.view :as change-pipeline]
   [frontend.view.modals.curtains.sales-opportunity.lost-curtain :as loss-curtain]
   [frontend.view.modals.curtains.sales-opportunity.on-hold-curtain :as on-hold-curtain]
   [frontend.view.modals.events :as modals]
   [frontend.view.nev.events :as events]
   [frontend.view.not-found :as not-found]
   [frontend.view.syncing-data :as syncing]
   [potpuri.core :refer [fn->]]
   [reagent.core :as r]
   [ui.modals.entity-selector.view :as entity-selector]))

(defn toolbar-buttons
  [{:keys [entity local-state*]}]
  (let [entity-id (:db/id entity)
        flagged? (contains? (:account/flagged-entities @session/account)
                            entity-id)]
    (concat
     []
     (when-not (session/is-read-only-user?)
       [(zap-toolbar/dropdown-button
         {:icon      :fa-star
          :on-select (partial common-actions/toggle-flag entity flagged?)
          :ord       1
          :label     (loc/tr (if flagged? :unflag-entity-menu-item :flag-entity-menu-item))})
        (zap-toolbar/dropdown-button
         {:icon             :fa-check
          :label            (loc/tr :won)
          :custom-style     "zap-btn__solid__green"
          :dropdown-color   "green"
          :dropdown-action? (not= (:sales-opportunity/status entity) :sales-opportunity.status/won)
          :on-select        (fn [_e]
                              (swap! local-state* assoc :won-modal-open? true)
                              #_(sales-opportunity-domain-actions/won! {:entity entity}))})
        (zap-toolbar/dropdown-button
         {:icon             :fa-ban
          :label            (loc/tr :lost)
          :custom-style     "zap-btn__solid__salmon"
          :dropdown-color   "salmon"
          :dropdown-action? (not= (:sales-opportunity/status entity) :sales-opportunity.status/lost)
          :on-select        (fn [_e]
                              (modals/open-modal!
                               loss-curtain/modal-key
                               {:sales-opportunity-id (:db/id entity)}))})
        (zap-toolbar/dropdown-button
         {:icon             :fa-pause
          :label            (loc/tr :put-on-hold)
          :custom-style     "zap-btn__solid__yellow"
          :dropdown-color   "yellow"
          :dropdown-action? (not= (:sales-opportunity/status entity)
                                  :sales-opportunity.status/on-hold)
          :on-select        (fn [_e]
                              (modals/open-modal!
                               on-hold-curtain/modal-key
                               {:sales-opportunity-id (:db/id entity)}))})
        (zap-toolbar/dropdown-button
         {:icon             :fa-redo-alt
          :label            (loc/tr :activate)
          :custom-style     "zap-btn__solid__blue"
          :dropdown-color   "blue"
          :dropdown-action? (not= (:sales-opportunity/status entity)
                                  :sales-opportunity.status/active)
          :hidden?          (fn []
                              (not (some #{(:sales-opportunity/status entity)}
                                         [:sales-opportunity.status/on-hold
                                          :sales-opportunity.status/lost])))
          :on-select        (fn []
                              (sales-opportunity-domain-actions/resurrect! entity))})])

     (when (and (not (:common/disabled? entity))
                (not (session/is-read-only-user?)))
       [(zap-toolbar/delete-button entity [:sales-opportunities-grid])])

     [(nev-components/audit-trail-button {:entity     entity
                                          :entity-ids [entity-id]})])))

(defn header-top
  [{:keys [entity read-only?]}]
  [:div
   {:class "flex gap-2"}
   [:span.entity-logo-container.icon-container
    [:i {:class-name (fu/cs :fal (entity/default-icon entity))}]]
   [:div.nev-name-row-container
    [nev-components/name-view
     {:entity     entity
      :read-only? read-only?}]]])

(def default-field-defs
  [{:fields       {0 {:field :sales-opportunity/description}
                   1 {:field :sales-opportunity/next-steps}}
    :field-layout :f-2-2}])

(defn header-board
  [{:keys [entity-atom local-state*]}]
  (let [header-state (r/atom {:edit-layout? false})
        set-edit-layout! (fn [val] (swap! header-state assoc :edit-layout? val))]
    (fn [_]
      (let [edit-layout? (:edit-layout? @header-state)
            tenant-schema @session/tenant-schema
            entity @entity-atom
            sales-oppo-id (:db/id entity)
            field-defs-path [:sales-opportunity :header :field-defs]
            field-defs-timeout-id :sales-oppo-field-defs
            field-defs (or (events/timeout-value field-defs-timeout-id
                                                 @(r/track session/get-view-config field-defs-path))
                           default-field-defs)
            selected-fields (->> field-defs
                                 (map :fields)
                                 (mapcat vals)
                                 (keep :field))
            overrides {:sales-opportunity/customer {:editor/type :editor.type/link
                                                    :read-only?  true}
                       :sales-opportunity/name     {:editor/type :editor.type/text-simple
                                                    :read-only?  true}
                       :sales-opportunity/stage    {:editor/type :editor.type/select
                                                    :read-only?  true}}
            remove-fields #{:sales-opportunity/ratings}
            sales-oppo-schema (templates/entity-type-template
                               {:entity-type     :entity.type/sales-opportunity
                                :selected-fields selected-fields
                                :remove-fields   remove-fields
                                :tenant-schema   @session/tenant-schema
                                :account         @session/account
                                :role            @session/role
                                :overrides       overrides})
            customer-schema (templates/customer-template
                             {:selected-fields (->> selected-fields
                                                    (filter vector?)
                                                    (filter (fn-> count (= 2)))
                                                    (filter (fn-> first (= :sales-opportunity/customer)))
                                                    (map last))
                              :tenant          @session/tenant
                              :tenant-schema   @session/tenant-schema
                              :account         @session/account
                              :role            @session/role
                              :overrides       {:investor/legal-entity {:editor/type :editor.type/link
                                                                        :read-only?  true}}})

            legal-entity-schema (templates/legal-entity-template
                                 {:selected-fields (->> selected-fields
                                                        (filter vector?)
                                                        (filter (fn-> count (= 3)))
                                                        (filter (fn-> first (= :sales-opportunity/customer)))
                                                        (filter (fn-> second (= :investor/legal-entity)))
                                                        (map last))
                                  :tenant          @session/tenant
                                  :tenant-schema   @session/tenant-schema
                                  :account         @session/account
                                  :role            @session/role})

            schema
            (doall
             (concat
              sales-oppo-schema
              (->> customer-schema
                   (map (fn [[k fs]]
                          ;;label "Customer (investor) - attr"
                          (let [ref-attribute [:sales-opportunity/customer k]]
                            [ref-attribute
                             (assoc fs
                                    :label
                                    (templates/customer-label tenant-schema fs))]))))
              (->> legal-entity-schema
                   (map
                    (fn [[k fs]]
                      ;;label "Customer (investor) - Legal entity (company) - attr"
                      (let [ref-attribute [:sales-opportunity/customer :investor/legal-entity k]]
                        [ref-attribute (assoc fs :label (templates/legal-entity-label fs))]))))))

            ;;test all fields - TODO remove:
            #_#_field-defs
              (->> schema
                   (map first)
                   (partition-all 4 4)
                         ;;(take 3)
                   (map (fn [[a b c d]]
                          {:fields       [{:field a}
                                          {:field b}
                                          {:field c}
                                          {:field d}]
                           :field-layout :f-1-1-1-1}))
                   vec)

            ;;customer changes: force save sales-oppo-id customer + customer changes + legal-entity changes
            ;;legal-entity-changes: force save customer legal-entity + legal-entity changes
            ;;wait until saves finish => re-initialize (wait-until-entity-sync - needed or not?)

            ;; or similar to entities-editor - fields are grouped by entity-id
            ;; [[oppo-id :common/created-on] [target-asset-id :common/created-on] ...]
            ;; or:
            ;; {oppo-id [:common/created-on :oppo/name ...]
            ;;  target-asset-id [:common/created-on
            ;;
            ]
        [:div
         [header/header-board
          {:top                  [header-top {:entity entity}]
           :preview-top          [header-top
                                  {:entity     entity
                                   :read-only? true}]
           :actions              (toolbar-buttons {:entity       entity
                                                   :local-state* local-state*})
           :entity-id            sales-oppo-id
           :entity-atom          entity-atom
           :edit-layout?         edit-layout?
           :set-edit-layout!     set-edit-layout!
           :field-defs           field-defs
           :schema               schema
           :editor-remove-fields [:sales-opportunity/customer
                                  :sales-opportunity/name
                                  [:sales-opportunity/customer :investor/name]
                                  [:sales-opportunity/customer :asset/name]
                                  ;;TODO remove all composite/calculated-fields:
                                  [:sales-opportunity/customer :person/name]
                                  [:sales-opportunity/customer :investor/legal-entity]
                                  [:sales-opportunity/customer :investor/legal-entity :person/name]
                                  [:sales-opportunity/customer :investor/legal-entity :asset/name]]
           :save-layout!         (fn [new-field-defs]
                                   (events/save-field-defs! field-defs-timeout-id
                                                            field-defs-path
                                                            new-field-defs
                                                            (fn [])
                                                            (fn [err]
                                                              (logging/error
                                                               "Failed to save field layout"
                                                               err))))}]]))))

(defn new-header?
  []
  (and (session/has-feature-access? :feature/customizable-nev)
       @session/customizable-nev?))

(defn sales-opportunity-view
  [{:keys [sales-opportunity-id]}]
  (let [state-atom (r/cursor app-db/app-state [:views :nev :sales-opportunity])
        initialized? (r/atom false)
        local-state* (r/atom {})
        temp-atom (r/cursor state-atom [:temp])
        view-state-atom (r/cursor state-atom [:view-state])
        error-atom (r/cursor state-atom [:error])
        notification-atom (r/cursor state-atom [:notification])
        events-pane-atom (r/cursor state-atom [:events-pane])
        entity-atom (r/cursor session/entities [sales-opportunity-id])
        sales-opportunity (get @session/entities sales-opportunity-id)
        original-pipeline-id (:sales-opportunity/pipeline sales-opportunity)
        capman-tenant? (tenant-customizations/capman?)]
    (r/create-class
     {:component-did-update
      (partial entity-utils/component-did-update
               :sales-opportunity-id
               (partial events/clear-errors! error-atom notification-atom))
      :component-did-mount
      (fn [_]
        (entity-utils/reset-state! state-atom sales-opportunity-id
                                   {:always-fetch? (session/new-grid-2023?)})
        (reset! initialized? true)
        (swap! session/selected-pipelines assoc
               :sales-opportunity-pipeline
               original-pipeline-id))
      :component-will-unmount
      (fn [_]
        (events/clear-errors! error-atom notification-atom)
        (entity-utils/clear-state! state-atom))
      :reagent-render
      (fn [{:keys [sales-opportunity-id] :as view-args}]
        (let [sales-opportunity (get @session/entities sales-opportunity-id)
              original-pipeline-id (:sales-opportunity/pipeline sales-opportunity)
              pipeline (session/get-entity original-pipeline-id)
              header-fields [:sales-opportunity/description :sales-opportunity/next-steps]          ;; TODO: configuration for user
              check-list-fields (->> pipeline
                                     :pipeline/checklist-configurations
                                     (mapcat (fn [checklist-id]
                                               (let [checklist-entity (session/get-entity (or (:db/id checklist-id)
                                                                                              checklist-id))]
                                                 (when (= (:check-list/target-entity-type checklist-entity)
                                                          (:entity/type sales-opportunity))
                                                   (->> checklist-entity
                                                        :check-list/configuration)))))
                                     (distinct))]
          (cond
            (not @initialized?)
            [syncing/view]
            (or (:common/disabled? sales-opportunity)
                (:not-found @state-atom))
            [not-found/not-found-content]
            (:loading @state-atom)
            [syncing/view]
            :else
            (if (or (not sales-opportunity)
                    (:common/disabled? sales-opportunity))
              [:div " "]
              (let [tenant-schema @session/tenant-schema
                    view-id :sales-opportunity
                    module-collapsed-path (fn [module-id]
                                            [view-id :modules :module-config module-id
                                             :collapsed?])]
                [:<>
                 (when (:won-modal-open? @local-state*)
                   [entity-selector/view
                    {:title                  (loc/tr :select-or-create-agreement)
                     :entity-types           [:entity.type/sale-agreement]
                     :new-entity-start-value (sales-opportunity-domain-actions/agreement-start-value sales-opportunity)
                     :label                  (loc/tr :entity.type/sale-agreement)
                     :on-select!             (fn [agreement-id]
                                               (swap! local-state* assoc
                                                      :won-modal-open? false)
                                               (sales-opportunity-actions/*->won!
                                                {:sales-opportunity-id (:db/id sales-opportunity)
                                                 :sale-agreement-id    agreement-id}
                                                (fn [_])
                                                (fn [err]
                                                  (logging/error "Failed to set sales oppo as Won " err))))
                     :on-cancel!             (fn [_]
                                               (swap! local-state* assoc :won-modal-open? false))}])
                 (when-not (session/is-guest?)
                   [zap-toolbar/nev-toolbar
                    {:state           state-atom
                     :label           (partial zap-toolbar/toolbar-back
                                               [:sales-opportunities-grid]
                                               (loc/tr :back-to/sales-opportunities-grid))
                     :actions         (toolbar-buttons (assoc view-args
                                                              :local-state* local-state*
                                                              :entity
                                                              sales-opportunity))
                     :dropdown-toggle [:span (loc/tr :actions)
                                       [:i.fas.fa-angle-down.ml-2]]
                     :dropdown-toggle-class-name
                     "zap-button zap-button--primary-btn actions-dropdown"}])
                 [:div.entity__container
                  (when @error-atom
                    [nev-components/error-bar error-atom])
                  (when @notification-atom
                    [nev-components/notification-bar notification-atom])

                  [:div.entity
                   [:div
                    {:class "w-full max-w-[1920px] mx-auto p-4"}
                    [nev-components/owner-stage-rating-module
                     {:entity-cursor            entity-atom
                      :view-state               view-state-atom
                      :entity                   sales-opportunity
                      :entity-stage-attribute   :sales-opportunity/stage
                      :stage-entity-type        :entity.type/sales-opportunity-stage
                      :stage-pipeline-id        (:sales-opportunity/pipeline sales-opportunity)
                      :stage-pipeline-attribute :sales-opportunity-stage/pipeline
                      :ratings-attribute        :sales-opportunity/ratings
                      :owner-attribute          :sales-opportunity/owner
                      :members-attribute        :common/team-members}]
                    (if (new-header?)
                      [header-board {:entity-atom  entity-atom
                                     :local-state* local-state*}]
                      [nev-components/header-module
                       {:component-state        temp-atom
                        :related-tags-entity-id (:sales-opportunity/customer sales-opportunity)
                        :view-state             view-state-atom
                        :entity                 sales-opportunity
                        :pipeline-id            original-pipeline-id
                        :selected-fields        header-fields}])
                    [:div.md:grid.grid-cols-10.gap-4
                     [:div
                      {:class (if (session/event-pane-disabled?)
                                "col-span-10"
                                "col-span-10 lg:col-span-5")}
                      [:div.entity-left
                       [modules/view
                        {:view-id             view-id
                         :ordering            (get-in @session/view-config [view-id :modules :ordering])
                         :save-modules-order! (partial events/save-modules-order!
                                                       (:entity/type sales-opportunity))
                         :modules
                         (concat
                          [{:module-id              :sales-opportunity-information
                            :header                 (loc/tr :nev-module/about-this-entity-type
                                                            [(string/lower-case (loc/tr (:entity/type
                                                                                         sales-opportunity)))])
                            ;;TODO uncomment when finished:
                            #_:header-tools
                            #_(when (session/is-admin?)
                                [:span.entity-editor-header-settings
                                 {:on-click (fn [e]
                                              ;;open settings
                                              )}
                                 [:span (loc/tr :entity-editor/settings-label)]
                                 [:i.fal.fa-cog]])
                            :collapsed?             (get-in @session/view-config
                                                            (module-collapsed-path
                                                             :sales-opportunity-information))
                            :save-module-collapsed! (partial events/save-module-collapsed!
                                                             :entity.type/sales-opportunity)
                            :content
                            [:div
                             [entity-editor/view
                              {:state-atom    state-atom
                               :entity-id     sales-opportunity-id
                               :view-id       view-id
                               :module-id     :sales-opportunity-information
                               :custom-save!  (fn [attribute new-value on-success! on-error!]
                                                (when (= attribute :sales-opportunity/pipeline)
                                                  (modals/open-modal!
                                                   change-pipeline/modal-key
                                                   {:ofs-opportunity      sales-opportunity
                                                    :selected-pipeline-id new-value
                                                    :original-pipeline-id original-pipeline-id
                                                    :on-close!            (on-success!)})
                                                  ;; call pipeline select error! to revert select value
                                                  (on-error!)
                                                  true))
                               :remove-fields (concat [:sales-opportunity/name
                                                       :sales-opportunity/agreement
                                                       :sales-opportunity/credit
                                                       :common/team-members
                                                       :sales-opportunity/customer
                                                       :common/network-drive :common/named-network-drives]
                                                      (when (tenant-customizations/capman?)
                                                        [:sales-opportunity/pipeline])
                                                      (field/entity-tag-fields tenant-schema
                                                                               (:entity/type
                                                                                sales-opportunity))
                                                      header-fields
                                                      check-list-fields)
                               :ordering      (get-in
                                               @session/view-config
                                               [view-id :modules :module-config
                                                :sales-opportunity-information :ordering])}]]}
                           (let [module-id :sales-customer
                                 customer-entity-type (->> sales-opportunity
                                                           :sales-opportunity/customer
                                                           (get @session/entities)
                                                           :entity/type)
                                 pipeline-customer-types (->> pipeline
                                                              :pipeline/target-asset-types)
                                 order-config-path [view-id :modules :module-config
                                                    module-id :ordering]
                                 module-label (cond
                                                (:sales-opportunity/customer sales-opportunity)
                                                (loc/tr :nev-module/existing-sales-customer
                                                        [(loc/tr customer-entity-type)])
                                                capman-tenant?
                                                (loc/tr :nev-module/sales-opportunity-investor)
                                                :else
                                                (loc/tr :nev-module/sales-opportunity-customer))
                                 collapsed-path (module-collapsed-path module-id)
                                 collapsed? (get-in @session/view-config collapsed-path)
                                 header-remove-tooltip (when capman-tenant?
                                                         (loc/tr :remove- [(loc/tr :investor-lc)]))
                                 remove-confirmation-body (when capman-tenant?
                                                            (loc/tr :remove- [(loc/tr :investor-lc)]))
                                 header-tools
                                 (when (and (:sales-opportunity/customer sales-opportunity)
                                            (not collapsed?)
                                            (not (session/is-read-only-user?)))
                                   [:span.grow.flex.justify-end
                                    [nev-components/remove-attribute-icon
                                     {:entity            sales-opportunity
                                      :attribute         :sales-opportunity/customer
                                      :tooltip-text      header-remove-tooltip
                                      :confirmation-body remove-confirmation-body}]])]
                             (nev-components/entity-relation-module
                              {:state-atom             state-atom
                               :module-id              module-id
                               :view-id                view-id
                               :ordering               (get-in
                                                        @session/view-config
                                                        order-config-path)
                               :order-config-path      order-config-path
                               :module-label           module-label
                               :entity                 sales-opportunity
                               :header-tools           header-tools
                               :override-editor-types  (when (= :entity.type/person
                                                                customer-entity-type)
                                                         {:person/name :editor.type/link})
                               :forced-entity-types pipeline-customer-types
                               :pipeline-id            (->> sales-opportunity
                                                            :sales-opportunity/pipeline)
                               :relationship-attribute :sales-opportunity/customer}))]
                          (when-not (session/is-guest?)
                            (associated-entities/modules
                             {:entity-id              sales-opportunity-id
                              :save-module-collapsed! events/save-module-collapsed!
                              :module-collapsed-path  module-collapsed-path}))

                          (when (not (session/is-guest?))
                            (->> pipeline
                                 :pipeline/checklist-configurations
                                 (keep (fn [checklist-id]
                                         (let [checklist-entity (session/get-entity (or (:db/id checklist-id)
                                                                                        checklist-id))
                                               module-id (keyword (str "check-list-" checklist-id))]
                                           (when (= (:check-list/target-entity-type checklist-entity)
                                                    (:entity/type sales-opportunity))
                                             {:module-id              module-id
                                              :header                 (:check-list/label checklist-entity)
                                              :collapsed?             (get-in @session/view-config (module-collapsed-path module-id))
                                              :save-module-collapsed! (partial events/save-module-collapsed! (:entity/type sales-opportunity))
                                              :start-collapsed?       true
                                              :header-tools           [check-list-module/header-view tenant-schema sales-opportunity-id checklist-id]
                                              :content                [:div
                                                                       [check-list-module/view
                                                                        {:parent-entity-id    sales-opportunity-id
                                                                         :checklist-entity-id checklist-id
                                                                         :fields              [{:attribute :role/guest-buy-side
                                                                                                :label     (loc/tr :buy-side-guests)}]}]]}))))))

                          [(when (session/has-feature-access? :feature/entity-esign)
                             (let [module-id :entity-esign]
                               (nev-components/entity-esign-module
                                {:view-id      view-id
                                 :module-id    module-id
                                 :module-label (loc/tr :nev-module/entity-esign)
                                 :entity       sales-opportunity})))]

                          [(when (and (not (session/is-mobile?))
                                      (f-permissions/field-read-access?
                                       :common/attachments
                                       :entity.type/sales-opportunity
                                       (->> sales-opportunity
                                            :sales-opportunity/pipeline)))
                             (nev-components/attachments-module view-id
                                                                :sales-opportunity-attachments
                                                                sales-opportunity))])}]]]
                     (when-not (session/event-pane-disabled?)
                       [:div
                        {:class "col-span-10 lg:col-span-5"}
                        [event-pane/view
                         {:state       events-pane-atom
                          :entity-ids  [sales-opportunity-id]
                          :module-args {:state-atom state-atom
                                        :view-id    view-id}}]])]]]]])))))})))

(defmethod routing/render-view :entity.type/sales-opportunity
  [{:keys [route-params _view-params]}]
  (let [sales-opportunity-id (-> route-params
                                 :id
                                 js/parseInt)]
    [default-page-layout/view
     {:class-name "zap-page sales-opportunity"}
     ;;the key change forces rerendering of the component (and its child components)
     ^{:key (str "sales-opportunity-viewer-id-" sales-opportunity-id)}
     [sales-opportunity-view {:sales-opportunity-id sales-opportunity-id}]]))
